import { apiRequest } from './client';
import type { UserProfile, Preferences } from '@outlinked/shared';

// Re-export canonical entity types for page consumers.
export type { UserProfile, Preferences };

// UserItem is what /users (list) returns per row — the masked-projection
// of UserProfile plus admin-portal projections (suspended, planTier)
// added by the handler. Backend may or may not populate these depending
// on whether they're stored on the User row directly; SPA treats them
// as optional.
export interface UserItem extends UserProfile {
  suspended?: boolean;
  planTier?: string;
}

export interface UsersListResponse {
  items: UserItem[];
  nextCursor: string | null;
}

// Resume metadata as returned by GET /users/:id — the admin-api strips
// the parsed metadata blob to just the audit-relevant top-level fields
// (key + size + upload time) so the SPA can show "42 KB pdf uploaded
// June 15" without piping the whole Resume row through masking.
export interface ResumeMetadata {
  resumeKey: string;
  sizeBytes: number;
  uploadedAt: string;
}

// AnswerBank entry as returned by GET /users/:id — flattened from the
// section-record + custom-array shape on disk into a single uniform
// array with a `section` tag per entry. See flattenAnswerBank in
// apps/admin-api/src/handlers/users.ts.
export interface AnswerBankEntry {
  section: 'personal' | 'employment' | 'experience' | 'general' | 'custom';
  question: string;
  answer: string;
}

export interface UserDetailResponse {
  user: UserItem;
  preferences: Preferences | null;
  answerBank: AnswerBankEntry[];
  resume: ResumeMetadata | null;
}

export interface ListUsersOpts {
  cursor?: string;
  limit?: number;
  search?: string;
}

export interface GetUserOpts {
  reveal?: string;
  reason?: string;
}

export function listUsers(opts: ListUsersOpts = {}): Promise<UsersListResponse> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.search) params.set('search', opts.search);
  return apiRequest<UsersListResponse>(`/users?${params}`);
}

export function getUser(id: string, opts: GetUserOpts = {}): Promise<UserDetailResponse> {
  const params = new URLSearchParams();
  if (opts.reveal) params.set('reveal', opts.reveal);
  if (opts.reason) params.set('reason', opts.reason);
  return apiRequest<UserDetailResponse>(`/users/${id}?${params}`);
}

export function editPreferences(
  id: string,
  patch: Record<string, unknown>,
  reason: string,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(
    `/users/${id}/preferences?reason=${encodeURIComponent(reason)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export function changeSubscription(
  id: string,
  tier: string,
  reason: string,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/users/${id}/subscription`, {
    method: 'PATCH',
    body: JSON.stringify({ tier, reason }),
  });
}

export function suspendUser(id: string, reason: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/users/${id}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function unsuspendUser(id: string, reason: string): Promise<{ ok: true }> {
  // Server route is DELETE /users/:id/suspend (unsuspend as "delete
  // the suspension row"). Reason ships in the body for the audit
  // trail. Pre-fix this helper posted to /users/:id/unsuspend which
  // didn't match any registered route — button would 404 silently
  // (#579).
  return apiRequest<{ ok: true }>(`/users/${id}/suspend`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
}

export function deleteUser(id: string, reason: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/users/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
}

export function listUserRuns(
  id: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<{ items: unknown[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  return apiRequest(`/users/${id}/runs?${params}`);
}

export function listUserAudit(id: string): Promise<{ items: unknown[] }> {
  return apiRequest(`/users/${id}/audit`);
}

export function listUserNotes(id: string): Promise<{ items: unknown[] }> {
  return apiRequest(`/users/${id}/notes`);
}

export function createNote(id: string, content: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/users/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}
